import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const NotificationsPanel = ({ notifications }) => {
  const [filter, setFilter] = useState('all');

  const notificationData = [
    {
      id: 1,
      type: 'compliance',
      title: 'KYC Verification Complete',
      message: 'Your identity verification has been successfully completed. You can now access all features.',
      timestamp: '2024-11-18 10:30:00',
      read: false,
      priority: 'high',
      actionRequired: false
    },
    {
      id: 2,
      type: 'transaction',
      title: 'Transfer Completed',
      message: 'Your transfer of $1,250.00 to Maria Garcia has been successfully processed.',
      timestamp: '2024-11-17 16:45:00',
      read: true,
      priority: 'medium',
      actionRequired: false
    },
    {
      id: 3,
      type: 'security',
      title: 'New Device Login',
      message: 'A new device (Chrome on Android) has accessed your account from Brooklyn, NY.',
      timestamp: '2024-11-16 14:20:00',
      read: true,
      priority: 'medium',
      actionRequired: false
    },
    {
      id: 4,
      type: 'compliance',
      title: 'Document Re-upload Required',
      message: 'Your selfie verification was rejected. Please upload a clearer photo holding your ID.',
      timestamp: '2024-11-15 11:15:00',
      read: false,
      priority: 'high',
      actionRequired: true
    },
    {
      id: 5,
      type: 'system',
      title: 'Maintenance Scheduled',
      message: 'System maintenance is scheduled for November 20, 2024 from 2:00 AM to 4:00 AM EST.',
      timestamp: '2024-11-14 09:00:00',
      read: true,
      priority: 'low',
      actionRequired: false
    },
    {
      id: 6,
      type: 'regulatory',
      title: 'New Compliance Requirements',
      message: 'Updated AML regulations require additional source of funds documentation for transfers above $10,000.',
      timestamp: '2024-11-13 15:30:00',
      read: false,
      priority: 'medium',
      actionRequired: true
    }
  ];

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'compliance':
        return 'Shield';
      case 'transaction':
        return 'ArrowRightLeft';
      case 'security':
        return 'Lock';
      case 'system':
        return 'Settings';
      case 'regulatory':
        return 'FileText';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return 'text-error bg-error/10 border-error/20';
    
    switch (type) {
      case 'compliance':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'transaction':
        return 'text-success bg-success/10 border-success/20';
      case 'security':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'system':
        return 'text-muted-foreground bg-muted border-border';
      case 'regulatory':
        return 'text-secondary bg-secondary/10 border-secondary/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error bg-error/10 border-error/20';
      case 'medium':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'low':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredNotifications = notificationData?.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification?.read;
    if (filter === 'action_required') return notification?.actionRequired;
    return notification?.type === filter;
  });

  const unreadCount = notificationData?.filter(n => !n?.read)?.length;
  const actionRequiredCount = notificationData?.filter(n => n?.actionRequired)?.length;

  const filterOptions = [
    { value: 'all', label: 'All', count: notificationData?.length },
    { value: 'unread', label: 'Unread', count: unreadCount },
    { value: 'action_required', label: 'Action Required', count: actionRequiredCount },
    { value: 'compliance', label: 'Compliance', count: notificationData?.filter(n => n?.type === 'compliance')?.length },
    { value: 'transaction', label: 'Transactions', count: notificationData?.filter(n => n?.type === 'transaction')?.length },
    { value: 'security', label: 'Security', count: notificationData?.filter(n => n?.type === 'security')?.length }
  ];

  const handleMarkAllRead = () => {
    // Simulate marking all as read
    console.log('Marking all notifications as read');
  };

  const handleMarkAsRead = (notificationId) => {
    // Simulate marking specific notification as read
    console.log(`Marking notification ${notificationId} as read`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
            <p className="text-sm text-muted-foreground">Stay updated with important account activities</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              iconName="CheckCheck"
              iconPosition="left"
              iconSize={16}
            >
              Mark All Read
            </Button>
          )}
          <div className="flex items-center space-x-1 text-sm text-muted-foreground">
            <Icon name="Bell" size={16} />
            <span>{unreadCount} unread</span>
          </div>
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex items-center space-x-1 mb-6 overflow-x-auto">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              filter === option?.value
                ? 'text-primary bg-primary/10 border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <span>{option?.label}</span>
            {option?.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                filter === option?.value
                  ? 'bg-primary text-white' :'bg-muted-foreground text-white'
              }`}>
                {option?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Notifications List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredNotifications?.length > 0 ? (
          filteredNotifications?.map((notification) => (
            <div
              key={notification?.id}
              className={`p-4 rounded-lg border transition-all hover:shadow-sm ${
                notification?.read ? 'bg-background' : 'bg-primary/5 border-primary/20'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification?.type, notification?.priority)}`}>
                  <Icon name={getNotificationIcon(notification?.type)} size={16} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-medium text-foreground truncate">{notification?.title}</h4>
                    <div className="flex items-center space-x-2 ml-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notification?.priority)}`}>
                        {notification?.priority}
                      </span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(notification?.timestamp)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getNotificationColor(notification?.type, 'low')}`}>
                        {notification?.type?.replace('_', ' ')}
                      </span>
                      {notification?.actionRequired && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium text-error bg-error/10 border border-error/20">
                          Action Required
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!notification?.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkAsRead(notification?.id)}
                          iconName="Check"
                          iconSize={14}
                        />
                      )}
                      {notification?.actionRequired && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="ExternalLink"
                          iconPosition="left"
                          iconSize={14}
                        >
                          Take Action
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-medium text-foreground mb-1">No notifications</h3>
            <p className="text-sm text-muted-foreground">
              {filter === 'all' ? 'You\'re all caught up!' : `No ${filter?.replace('_', ' ')} notifications found.`}
            </p>
          </div>
        )}
      </div>
      {/* Notification Settings */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground">Notification Preferences</h4>
            <p className="text-sm text-muted-foreground">Manage how you receive notifications</p>
          </div>
          <Button
            variant="outline"
            iconName="Settings"
            iconPosition="left"
            iconSize={16}
          >
            Manage Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPanel;